import { getDeleteApiDeviceIdMutationOptions, type DeviceViewDTO } from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Icon, openModal } from '@smartsash/ui';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { RowActions as Shared, type RowActionEntry } from '@/ui/shared/RowActions';

function RowActions({ row }: { row: DeviceViewDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // HS-869: the card/menu read the DTO the list query returns.
  const id = row.id ?? '';
  // HS-847: delete via server-ops (invalidates the device list query + toast).
  const del = useEntityDelete({
    entity: 'device',
    mutationOptions: getDeleteApiDeviceIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
  });

  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally
  // rendered dialog. It resolves with the pressed action's `value`, or `null` when
  // dismissed via the overlay, Escape or the close button.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('devices.deleteTitle', { name: row.model }),
      description: t('devices.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ id });
  }, [del, id, row.model, t]);

  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      permission: 'device.edit',
      icon: 'edit-01',
      onClick: () => {
        void navigate({ to: '/devices/$id/edit', params: { id } });
      },
    },
    null,
    {
      label: t('devices.actions.viewLaboratory'),
      icon: 'lab',
      onClick: () => {
        void navigate({ to: '/laboratories/$id', params: { id: row.labId ?? '' } });
      },
    },
    null,
    {
      label: t('common.delete'),
      permission: 'device.delete',
      icon: 'trash-01',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return (
    <>
      <Shared items={items} />
    </>
  );
}

export default RowActions;
